import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import { NitroLogo } from 'nitro-components';

import Breakpoint from '../../../../utils/breakpoints';
import Accordion from './Accordion';
import MenuRoute from './MenuRoute';
import NestedMenuRoutes from './NestedMenuRoutes';
import { getHeaderMenu } from '../../../../layouts/selector';
import { SERVICENOW_OUR_SERVICES } from '../../../../api/const';
import {
  DesktopHeader,
  HeaderIcon,
  HeaderLogo,
  HeaderTitle,
  MobileHeader,
  MenuContainer,
  MenuContent
} from './styles';
import ServiceNowDialog from '../../../../features/Servicenow/ServiceNowDialog';
import InsightAPI from '../../../../api/InsightsApi';
/**
 * Determine if the given menu is active based on the current browser route.
 *
 * @param pathname Current active browser pathname
 * @param menu The menu to check if it is active
 *
 * @returns The id of the menu if it is active.
 */
const getActiveMenu = (pathname, menu) => {
  if (!menu.children.length && menu.route && pathname.includes(menu.route)) {
    return menu.id;
  }
  for (let i = 0; i < menu.children.length; i += 1) {
    if (getActiveMenu(pathname, menu.children[i])) {
      return menu.id;
    }
  }
  return false;
};

const openServicenowOurService = () => {
  window.open(SERVICENOW_OUR_SERVICES, '_blank');
};

/**
 * This component displays a sidebar for application navigation based on a given list of menus.
 * When opened, a floating element will expand and slide out.
 */
class Menu extends React.Component {
  state = {
    openServiceNow: false,
    openAccordionId: null
  };

  componentDidUpdate(prevProps) {
    // Opening the menu
    if (!prevProps.open && this.props.open) {
      let activeAccordionId = null;
      this.props.menus.forEach(menu => {
        activeAccordionId =
          getActiveMenu(this.props.location.pathname, menu) ||
          activeAccordionId;
      });
      this.setState({ openAccordionId: activeAccordionId });
    }

    // Closing the menu
    if (prevProps.open && !this.props.open) {
      this.setState({ openAccordionId: null });
    }
  }

  openServiceNowDialog = () => {
    this.setState({ openServiceNow: true });
    this.props.onClose();
  };

  closeServiceNowDialog = () => {
    this.setState({ openServiceNow: false });
  };

  downloadContent = menu => {
    let url = menu.route || menu.url;
    url = url.replace('api://', '');
    InsightAPI.getGenericInsightDataFile(url);
    this.props.onClose();
  };

  render() {
    const {
      location,
      menus,
      homeRoute,
      homeIcon,
      home,
      open,
      hasServiceNow,
      openedWidth,
      closedWidth,
      onClose
    } = this.props;

    const headerIcon = open ? 'arrow-left' : 'menu';

    // Determine the ID of the active menu, defaults to the ID of the home menu
    let activeId = home;
    menus.forEach(menu => {
      activeId = getActiveMenu(location.pathname, menu) || activeId;
    });

    const navMenus = [
      {
        id: home,
        route: homeRoute,
        icon: homeIcon,
        title: home,
        children: []
      },
      ...menus
    ];
    const ACCIDENT_MANAGEMENT_TITLE = 'Accidents Dashboard';
    const FBT_TAX_REPORT_API_REGEXP = /^api:\/\//;
    // Only show the header border if the first menu row is not selected. This is to prevent a border between the
    // header icon color and the active row color
    const showHeaderBorder = navMenus.length && activeId !== navMenus[0].id;

    const isActionMenu = actmenu => {
      const to = actmenu.route || actmenu.url;

      if (actmenu.title === ACCIDENT_MANAGEMENT_TITLE && !hasServiceNow) {
        return true;
      }

      // Links to download
      if (FBT_TAX_REPORT_API_REGEXP.test(to)) {
        return true;
      }

      return false;
    };

    return (
      <MenuContainer
        open={open}
        openedWidth={openedWidth}
        closedWidth={closedWidth}
      >
        <Breakpoint name="desktop">
          <DesktopHeader width={openedWidth}>
            <HeaderLogo to={homeRoute}>
              <NitroLogo />
            </HeaderLogo>
            <HeaderIcon
              name={headerIcon}
              variant="white"
              size={20}
              action={this.props.onClose}
            />
          </DesktopHeader>
        </Breakpoint>

        <Breakpoint name="mobileTablet">
          <MobileHeader showHeaderBorder={showHeaderBorder}>
            <HeaderTitle>Menu</HeaderTitle>
            <HeaderIcon
              name={headerIcon}
              variant="white"
              size={20}
              action={this.props.onClose}
            />
          </MobileHeader>
        </Breakpoint>
        <MenuContent>
          {navMenus.map(menu => {
            const active = menu.id === activeId;

            const handleClick = menuItem => {
              if (
                menuItem.title === ACCIDENT_MANAGEMENT_TITLE &&
                !hasServiceNow
              ) {
                return this.openServiceNowDialog();
              }

              // Links to download
              const to = menuItem.route || menuItem.url;
              if (FBT_TAX_REPORT_API_REGEXP.test(to)) {
                return this.downloadContent(menuItem);
              }
            };

            if (!menu.children.length) {
              return (
                <MenuRoute
                  menu={menu}
                  key={menu.id}
                  active={active}
                  width={openedWidth}
                  isAction={isActionMenu}
                  handleRoute={handleClick}
                  onClose={onClose}
                />
              );
            }

            return (
              <Accordion
                icon={menu.icon}
                title={menu.title}
                key={menu.id}
                width={openedWidth}
                active={active}
                toggle={() => {
                  const openAccordionId =
                    this.state.openAccordionId === menu.id ? null : menu.id;
                  this.setState({ openAccordionId });
                }}
                open={menu.id === this.state.openAccordionId}
              >
                {menu.children.map(subMenu => (
                  <NestedMenuRoutes
                    menu={subMenu}
                    key={subMenu.id}
                    isAction={isActionMenu}
                    handleRoute={handleClick}
                    location={location}
                  />
                ))}
              </Accordion>
            );
          })}
        </MenuContent>
        <ServiceNowDialog
          open={this.state.openServiceNow}
          handleClose={this.closeServiceNowDialog}
          openServicenow={openServicenowOurService}
          desktop={openedWidth === '250px'}
        />
      </MenuContainer>
    );
  }
}

Menu.propTypes = {
  // Callback for closing the sidebar
  onClose: PropTypes.func,

  // `true` if the sidebar should be open
  open: PropTypes.bool,

  // A css value for the menu width while it is open
  openedWidth: PropTypes.string,

  // A css value for the menu width while it is closed
  closedWidth: PropTypes.string,

  // A list of menus that define available page navigation
  menus: PropTypes.arrayOf(PropTypes.object),

  // A Label for the home menu
  home: PropTypes.string,

  // The icon of the home menu
  homeIcon: PropTypes.string,

  // The route to direct to when selecting home
  homeRoute: PropTypes.string,

  // The react router current location
  location: PropTypes.shape({
    pathname: React.PropTypes.string.isRequired
  }).isRequired,

  // True if the user has the service now feature enabled
  hasServiceNow: PropTypes.bool
};

Menu.defaultProps = {
  onClose: () => {},
  open: false,
  openedWidth: '100vw',
  closedWidth: '0px',
  menus: [],
  home: 'Dashboard',
  homeIcon: undefined,
  homeRoute: '/',
  hasServiceNow: false
};

const mapStateToProps = state => ({
  menus: getHeaderMenu(state),
  home: state.auth.config.home,
  homeIcon: state.auth.config.icon,
  hasServiceNow: state.auth.config.hasServiceNow,
  homeRoute: state.auth.config.homeRoute
});

export default connect(mapStateToProps, {})(Menu);



// WEBPACK FOOTER //
// ./src/components/common/Menu/Sidebar/Menu.js